import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getNextQuizQuestion,
  isQuizComplete,
  isQuizDailyLimitReached,
  type QuizQuestion,
} from '../../../domain';
import {
  playDeliveryFail,
  playDeliverySuccess,
  playUiClick,
} from '../../assets/preloadSounds';
import { useAppStore } from '../../store/appStore';
import {
  createQuizScreenModel,
  type QuizAnswerFeedback,
  type QuizScreenProps,
} from './QuizScreenModelScreen';
import { QuizScreenController } from './QuizScreenController';
import { QuizScreenView } from './QuizScreenView';

export type { QuizScreenProps } from './QuizScreenModelScreen';

const ANSWER_FEEDBACK_MS = 3000;

export function QuizScreen({ onGoBack, onClueClick, ...restProps }: QuizScreenProps) {
  const playerEconomy = useAppStore((state) => state.playerEconomy);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const economyQuestion = useMemo(
    () => getNextQuizQuestion(playerEconomy),
    [playerEconomy],
  );
  const dailyLimitReached = isQuizDailyLimitReached(playerEconomy, nowMs);
  const quizBankEmpty = economyQuestion == null || isQuizComplete(playerEconomy);

  const [activeQuestion, setActiveQuestion] = useState<QuizQuestion | null>(economyQuestion);
  const [feedback, setFeedback] = useState<QuizAnswerFeedback | null>(null);
  const [showTicketReward, setShowTicketReward] = useState(false);
  const advanceTimerRef = useRef<number | null>(null);

  const showDailyLimit =
    !showTicketReward && feedback == null && dailyLimitReached && !quizBankEmpty;

  useEffect(() => {
    if (feedback != null || showTicketReward) {
      return;
    }
    setActiveQuestion(economyQuestion);
  }, [economyQuestion, feedback, showTicketReward]);

  useEffect(
    () => () => {
      if (advanceTimerRef.current != null) {
        window.clearTimeout(advanceTimerRef.current);
      }
    },
    [],
  );

  const finishAnswerFeedback = useCallback(() => {
    setFeedback(null);
    setShowTicketReward(false);
    const economy = useAppStore.getState().playerEconomy;
    setNowMs(Date.now());
    if (getNextQuizQuestion(economy) == null || isQuizComplete(economy)) {
      onGoBack?.();
    }
  }, [onGoBack]);

  const handleAnswer = useCallback(
    (index: number) => {
      if (
        feedback != null ||
        activeQuestion == null ||
        showTicketReward ||
        showDailyLimit
      ) {
        return;
      }

      const question = activeQuestion;
      const correct = index === question.correctIndex;

      playUiClick();
      if (correct) {
        playDeliverySuccess();
      } else {
        playDeliveryFail();
      }

      // Lock the current question before the store updates economy/next question,
      // otherwise the sync effect advances activeQuestion before feedback is set.
      setFeedback({
        selectedIndex: index,
        correctIndex: question.correctIndex,
        correct,
      });

      if (advanceTimerRef.current != null) {
        window.clearTimeout(advanceTimerRef.current);
      }

      void useAppStore
        .getState()
        .submitQuizAnswer(question.id, index)
        .then((result) => {
          setFeedback({
            selectedIndex: index,
            correctIndex: question.correctIndex,
            correct: result.correct,
          });
          if (result.correct && result.rewardPasses > 0) {
            setShowTicketReward(true);
          }

          advanceTimerRef.current = window.setTimeout(() => {
            advanceTimerRef.current = null;
            finishAnswerFeedback();
          }, ANSWER_FEEDBACK_MS);
        })
        .catch(() => {
          setFeedback(null);
          setShowTicketReward(false);
        });
    },
    [activeQuestion, feedback, finishAnswerFeedback, showDailyLimit, showTicketReward],
  );

  const handleDailyLimitZero = useCallback(() => {
    setNowMs(Date.now());
    void useAppStore.getState().refreshEconomy();
  }, []);

  const model = createQuizScreenModel({
    ...restProps,
    onGoBack,
    onClueClick,
    question: activeQuestion,
    feedback,
    showTicketReward,
    showDailyLimit,
    onAnswer: handleAnswer,
    onDailyLimitZero: handleDailyLimitZero,
  });
  const controller = new QuizScreenController(model);

  return <QuizScreenView model={model} controller={controller} />;
}
